import { ApolloCache, DocumentNode, useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';

import { evictCache } from '@neo.tax/shared/apollo';
import {
  GET_SYNCED_PROVIDERS,
  UPLOAD_ACCOUNTING_EXPENSE,
  UPLOAD_PAYROLL_EXPENSE,
  UploadAccountingExpenseCsvMutation,
  UploadPayrollExpenseCsvMutation,
  TabularDataParseError,
  GetSyncedProvidersQueryVariables,
  Query,
} from '@neo.tax/shared/graphql';

import { ConnectProviderStatus } from '../../enums';
import { ConnectProviderSlideProps } from '../../types';

type ProviderType = ConnectProviderSlideProps['provider']['providerType'];

interface ProviderConfig {
  providerType: ProviderType;
  providerPropName: string;
  mutation: DocumentNode;
  cacheKeys: (keyof Query)[];
  getData: (data: any) => any;
  getQres: (data: any) => any;
}

const PROVIDER_CONFIG: Record<ProviderType, ProviderConfig> = {
  payroll: {
    providerType: 'payroll',
    providerPropName: 'payrollProvider',
    mutation: UPLOAD_PAYROLL_EXPENSE,
    cacheKeys: ['getPayrollQres', 'getPayrollQreSummary'],
    getData: (data: UploadPayrollExpenseCsvMutation) => data?.uploadPayrollExpenseCsv,
    getQres: (data: UploadPayrollExpenseCsvMutation) => data?.uploadPayrollExpenseCsv?.payrollQres,
  },
  accounting: {
    providerType: 'accounting',
    providerPropName: 'accountingProvider',
    mutation: UPLOAD_ACCOUNTING_EXPENSE,
    cacheKeys: ['getAccountingQres', 'getAccountingQreSummary'],
    getData: (data: UploadAccountingExpenseCsvMutation) => data?.uploadAccountingExpenseCsv,
    getQres: (data: UploadAccountingExpenseCsvMutation) => data?.uploadAccountingExpenseCsv?.accountingQres,
  },
  // when props.providerType is ticketing, we use the accounting provider config
  // unsure if this is an actual use case, but it works for now
  ticketing: {
    providerType: 'accounting',
    providerPropName: 'accountingProvider',
    mutation: UPLOAD_ACCOUNTING_EXPENSE,
    cacheKeys: ['getAccountingQres', 'getAccountingQreSummary'],
    getData: (data: UploadAccountingExpenseCsvMutation) => data?.uploadAccountingExpenseCsv,
    getQres: (data: UploadAccountingExpenseCsvMutation) => data?.uploadAccountingExpenseCsv?.accountingQres,
  },
};

interface UseSyncCsvProviderConnection {
  connect: () => Promise<void>;
  connectStatus: ConnectProviderStatus;
  file?: File;
  setFile: (file?: File) => void;
  retry: () => void;
  errors?: TabularDataParseError[] | null;
}

export const useSyncCsvProvider = ({
  fiscalYearId,
  providerName,
  providerType,
}: ConnectProviderSlideProps['provider'] & { fiscalYearId: string }): UseSyncCsvProviderConnection => {
  const [connectStatus, setConnectStatus] = useState<ConnectProviderStatus>(ConnectProviderStatus.NOT_STARTED);
  const [file, setFile] = useState<File | undefined>();

  const config = PROVIDER_CONFIG[providerType];

  const [uploadCsv, { data, error }] = useMutation(config.mutation);

  const providerData = config.getData(data);
  const qres = config.getQres(data);

  const handleSetFile = (file: File | undefined): void => {
    setFile(file);
  };

  const retry = (): void => {
    setFile(undefined);
    setConnectStatus(ConnectProviderStatus.NOT_STARTED);
  };

  useEffect(() => {
    if (file) {
      setConnectStatus(ConnectProviderStatus.FILE_ADDED);
      return;
    }
    setConnectStatus(ConnectProviderStatus.NOT_STARTED);
  }, [file]);

  useEffect(() => {
    if (qres) {
      setConnectStatus(ConnectProviderStatus.SUCCESS);
    } else if (providerData?.errors || error) {
      setConnectStatus(ConnectProviderStatus.ERROR);
    }
  }, [error, providerData, qres]);

  const connect = async (): Promise<void> => {
    setConnectStatus(ConnectProviderStatus.SYNCING);

    const commonMutationConfig = {
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GET_SYNCED_PROVIDERS,
          variables: {
            fiscalYearId,
          } satisfies GetSyncedProvidersQueryVariables,
        },
      ],
    };

    try {
      await uploadCsv({
        variables: {
          fiscalYearId,
          file,
          [`${providerType}Provider`]: providerName,
        },
        update: (cache: ApolloCache<any>): void => {
          evictCache(cache, config.cacheKeys);
        },
        ...commonMutationConfig,
      });
    } catch {
      setConnectStatus(ConnectProviderStatus.ERROR);
    }
  };

  return {
    connect,
    connectStatus,
    file,
    setFile: handleSetFile,
    retry,
    errors: providerData?.errors,
  };
};
